import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button, Input, Label, MobileNumberInput, Modal } from '@components';
import { IconButton } from '@components/button/Button';
import CategoryInput from '@components/category-input/CategoryInput';
import { defaultCountryCode } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';
import SourceInput from '@components/source-input/SourceInput';
import { amountRegex } from '@constants/regex';
import { STORAGE_KEYS } from '@constants/storage';
import { isValidPhoneNumber } from '@helpers/generic';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';
import { isInvalidEmail } from '@utils/validations';
import { Category } from 'types/categoryInput';
import { LeadCategory } from 'types/lead-management';

import {
  INPUT_BASE_CLASSNAME,
  INPUT_FOCUS_CLASSNAME
} from '../constants/common';
import useLeadSources, { SourceSelectItem } from '../hooks/useLeadSources';
import {
  useCreateCategoryMutation,
  useCreateLeadSourceMutation,
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
  useUpdateLeadSourceMutation
} from '../queries/LeadManagementQuery';
import { ContactEditModalProps } from '../types/leadDetails';

const ContactEditModal = ({
  isOpen,
  onClose,
  leadData,
  onSave,
  isSaving = false
}: ContactEditModalProps) => {
  const { appConfig } = useSelector((state: any) => state.user);

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { data: categoriesData } = useGetCategoriesQuery(
    { organizationId },
    { refetchOnMountOrArgChange: true }
  );
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [createLeadSource] = useCreateLeadSourceMutation();
  const [updateLeadSource] = useUpdateLeadSourceMutation();
  const { sourceSelectItems, systemSourceNames, getSourceId } =
    useLeadSources();
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const getCountryId = (countryCode: string) =>
    (appConfig?.supported_phone_number_countries || []).find(
      (country: CountryData) => `${country.dial_code}` === countryCode
    )?.code;

  const [formData, setFormData] = useState({
    email_id: leadData?.email_id || '',
    mobile_number: leadData?.mobile_number || '',
    country_code: leadData?.country_code || defaultCountryCode,
    secondary_mobile_number: leadData?.secondary_mobile_number || '',
    secondary_country_code:
      leadData?.secondary_country_code || defaultCountryCode,
    country: leadData?.country || '',
    state: leadData?.state || '',
    city: leadData?.city || '',
    pincode: leadData?.pincode || '',
    deal_value: (leadData?.deal_value ?? 0) / 100 || 0,
    source: leadData?.source || '',
    category: leadData?.category?.id || '',
    description: leadData?.description || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiCategoryOptions, setApiCategoryOptions] = useState<Category[]>([]);
  const [categoryInput, setCategoryInput] = useState<string>('');
  const [sourceInput, setSourceInput] = useState<string>('');

  const isSourceDisabled = Boolean(leadData?.is_external_synced);

  useEffect(() => {
    if (isOpen && leadData) {
      setFormData({
        email_id: leadData?.email_id || '',
        mobile_number: leadData?.mobile_number || '',
        country_code: leadData?.country_code || defaultCountryCode,
        secondary_mobile_number: leadData?.secondary_mobile_number || '',
        secondary_country_code:
          leadData?.secondary_country_code || defaultCountryCode,
        country: leadData?.country || '',
        state: leadData?.state || '',
        city: leadData?.city || '',
        pincode: leadData?.pincode || '',
        deal_value: (leadData?.deal_value ?? 0) / 100 || 0,
        source: leadData?.source || '',
        category: leadData?.category?.id || '',
        description: leadData?.description || ''
      });
      setCategoryInput(leadData?.category?.value || '');
    }
  }, [isOpen, leadData]);

  useEffect(() => {
    if (Array.isArray(categoriesData) && categoriesData.length > 0) {
      const transformedCategories: Category[] = categoriesData.map(
        (categoryItem: LeadCategory) => ({
          id: String(categoryItem.id),
          name: categoryItem.value
        })
      );
      setApiCategoryOptions(transformedCategories);
    }
  }, [categoriesData]);

  const selectedSource = useMemo(
    () =>
      formData.source
        ? sourceSelectItems.find((item) => item.id === formData.source) || null
        : null,
    [formData.source, sourceSelectItems]
  );

  useEffect(() => {
    if (!formData.source) return;
    if (sourceSelectItems.some((item) => item.id === formData.source)) return;
    const resolvedId = getSourceId(formData.source);
    if (resolvedId) {
      setFormData((prev) => ({ ...prev, source: resolvedId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.source, sourceSelectItems]);

  useEffect(() => {
    if (selectedSource?.name) {
      setSourceInput(selectedSource.name);
    }
  }, [selectedSource]);

  const handleBlur = (field: keyof typeof formData) => {
    let message = '';
    const value = formData[field];

    switch (field) {
      case 'email_id':
        if (typeof value === 'string' && value && isInvalidEmail(value)) {
          message = 'Please enter a valid email address';
        }
        break;
      case 'mobile_number':
        if (
          typeof value === 'string' &&
          value &&
          !isValidPhoneNumber(value, getCountryId(formData.country_code))
        ) {
          message = 'Please enter a valid mobile number';
        }
        break;
      case 'secondary_mobile_number':
        if (
          typeof value === 'string' &&
          value &&
          !isValidPhoneNumber(
            value,
            getCountryId(formData.secondary_country_code)
          )
        ) {
          message = 'Please enter a valid mobile number';
        }
        break;
      case 'description':
        if (
          typeof value === 'string' &&
          value.trim() &&
          (value.trim().length < 3 || value.trim().length > 150)
        ) {
          message = 'Description must be between 3 and 150 characters.';
        }
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.mobile_number.trim()) {
      newErrors.mobile_number = 'Mobile number is required';
    } else if (
      !isValidPhoneNumber(
        formData.mobile_number,
        getCountryId(formData.country_code)
      )
    ) {
      newErrors.mobile_number = 'Please enter a valid mobile number';
    }

    if (formData.secondary_mobile_number) {
      if (
        !isValidPhoneNumber(
          formData.secondary_mobile_number,
          getCountryId(formData.secondary_country_code)
        )
      ) {
        newErrors.secondary_mobile_number =
          'Please enter a valid mobile number';
      } else if (
        formData.mobile_number === formData.secondary_mobile_number &&
        formData.country_code === formData.secondary_country_code
      ) {
        newErrors.secondary_mobile_number =
          'Alternate number cannot be the same as mobile number.';
      }
    }

    if (!formData.country_code.trim()) {
      newErrors.country_code = 'Country code is required';
    }

    if (formData.email_id && isInvalidEmail(formData.email_id)) {
      newErrors.email_id = 'Please enter a valid email address';
    }

    if (
      formData.description.trim() &&
      (formData.description.trim().length < 3 ||
        formData.description.trim().length > 150)
    ) {
      newErrors.description =
        'Description must be between 3 and 150 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCategoryChange = async (category: Category | null) => {
    if (!category) {
      handleInputChange('category', '');
      setCategoryInput('');
      return;
    }

    const isNewCategory = !apiCategoryOptions.find(
      (option) => option.id === category.id
    );

    if (isNewCategory) {
      try {
        const response = await createCategory({
          organizationId,
          name: category.name
        });

        if ('data' in response && response.data) {
          const newCategory = {
            id: response.data.id,
            name: response.data.value
          };
          setApiCategoryOptions([newCategory, ...apiCategoryOptions]);
          handleInputChange('category', newCategory.id);
          setCategoryInput(newCategory.name);
          createSuccessAlert(
            `Category "${response.data.value}" created successfully`
          );
        } else if ('error' in response) {
          createErrorAlert(`Failed to create category "${category.name}"`);
        }
      } catch (error) {
        createErrorAlert(
          `Failed to create category "${category.name}". Please try again.`
        );
      }
    } else {
      handleInputChange('category', category.id);
      setCategoryInput(category.name);
    }
  };

  const handleUpdateCategory = async (category: Category) => {
    try {
      const response = await updateCategory({
        id: category.id,
        value: category.name,
        organizationId
      });

      if ('data' in response && response.data) {
        setApiCategoryOptions(
          apiCategoryOptions.map((item: Category) =>
            item.id === category.id
              ? { id: response.data.id, name: response.data.value }
              : item
          )
        );

        if (formData.category === category.id) {
          setCategoryInput(response.data.value);
        }

        createSuccessAlert(
          `Category "${response.data.value}" updated successfully`
        );
      } else {
        createErrorAlert(`Failed to update category "${category.name}"`);
      }
    } catch (error) {
      createErrorAlert(
        `Failed to update category "${category.name}". Please try again.`
      );
    }
  };

  const handleSourceChange = async (source: SourceSelectItem | null) => {
    if (!source) {
      handleInputChange('source', '');
      setSourceInput('');
      return;
    }

    const isNewSource = !sourceSelectItems.find(
      (item) => item.id === source.id
    );

    if (isNewSource) {
      try {
        const response = await createLeadSource({
          organizationId,
          name: source.name
        });

        if ('data' in response && response.data) {
          handleInputChange('source', response.data.id);
          setSourceInput(response.data.name);
          createSuccessAlert(
            `Source "${response.data.name}" created successfully`
          );
        } else if ('error' in response) {
          createErrorAlert(`Failed to create source "${source.name}"`);
        }
      } catch (error) {
        createErrorAlert(
          `Failed to create source "${source.name}". Please try again.`
        );
      }
    } else {
      handleInputChange('source', source.id);
      setSourceInput(source.name);
    }
  };

  const handleUpdateSource = async (sourceItem: SourceSelectItem) => {
    try {
      const response = await updateLeadSource({
        organizationId,
        id: sourceItem.id,
        name: sourceItem.name
      });

      if ('data' in response && response.data) {
        createSuccessAlert(
          `Source "${response.data.name}" updated successfully`
        );
        handleInputChange('source', response.data.id);
        setSourceInput(response.data.name);
      } else {
        createErrorAlert(`Failed to update source "${sourceItem.name}"`);
      }
    } catch (error) {
      createErrorAlert(
        `Failed to update source "${sourceItem.name}". Please try again.`
      );
    }
  };

  const handlePhoneNumberChange =
    (field: 'mobile_number' | 'secondary_mobile_number') =>
    (value?: string) => {
      handleInputChange(field, value || '');
    };

  const handleCountryCodeChange =
    (field: 'country_code' | 'secondary_country_code') =>
    (countryCode: string) => {
      handleInputChange(field, countryCode);
    };

  const handleClose = () => {
    onClose();
    setErrors({});
  };

  const handleSave = () => {
    if (validateForm()) {
      const selectedCategory = apiCategoryOptions.find(
        (option) => option.id === formData.category
      );

      const updatedData = {
        ...formData,
        category: selectedCategory
          ? {
              id: selectedCategory.id,
              value: selectedCategory.name || ''
            }
          : undefined
      };

      onSave(updatedData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleClose}
      className="w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl"
    >
      <div className="p-6">
        <div className="mb-6 flex items-start justify-between gap-2">
          <h2 className="min-w-0 break-words text-xl font-bold text-content">
            Edit Lead Details - {leadData?.name}
          </h2>
          <IconButton
            iconName="close"
            variant="transparent"
            buttonSize="small"
            handleClick={handleClose}
            className="shrink-0 rounded-full hover:bg-gray-100"
          />
        </div>

        <div className="mb-6">
          <h3 className="mb-4 text-base font-medium text-gray-900">
            Personal Information
          </h3>

          <div className="mb-4">
            <Label label="Email ID" />
            <Input
              value={formData.email_id}
              handleInputChange={(value) =>
                handleInputChange('email_id', value)
              }
              handleInputBlur={() => handleBlur('email_id')}
              placeholder="Enter Email ID"
              type="email"
              error={!!errors.email_id}
              errorMessage={errors.email_id}
              inputStyle={INPUT_BASE_CLASSNAME}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <MobileNumberInput
              label="Mobile Number"
              isRequired
              countryCode={formData.country_code}
              phoneNumber={formData.mobile_number}
              onPhoneNumberChange={handlePhoneNumberChange('mobile_number')}
              onPhoneNumberBlur={() => handleBlur('mobile_number')}
              error={!!errors.mobile_number}
              errorMessage={errors.mobile_number}
              placeholder="Enter mobile number"
              onCountryCodeChange={handleCountryCodeChange('country_code')}
              disabled={leadData?.is_onboarded}
            />

            <MobileNumberInput
              label="Alternate Number"
              countryCode={formData.secondary_country_code}
              phoneNumber={formData.secondary_mobile_number}
              onPhoneNumberChange={handlePhoneNumberChange(
                'secondary_mobile_number'
              )}
              onPhoneNumberBlur={() => handleBlur('secondary_mobile_number')}
              error={!!errors.secondary_mobile_number}
              errorMessage={errors.secondary_mobile_number}
              placeholder="Enter alternate number"
              onCountryCodeChange={handleCountryCodeChange(
                'secondary_country_code'
              )}
            />

            <div>
              <Label label="Country" />
              <Input
                value={formData.country}
                handleInputChange={(value) =>
                  handleInputChange('country', value)
                }
                placeholder="Enter country"
                type="text"
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>

            <div>
              <Label label="State" />
              <Input
                value={formData.state}
                handleInputChange={(value) => handleInputChange('state', value)}
                placeholder="Enter state"
                type="text"
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>

            <div>
              <Label label="City" />
              <Input
                value={formData.city}
                handleInputChange={(value) => handleInputChange('city', value)}
                placeholder="Enter city"
                type="text"
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>

            <div>
              <Label label="Pin Code" />
              <Input
                value={formData.pincode}
                handleInputChange={(value) =>
                  handleInputChange('pincode', value)
                }
                placeholder="Enter pincode"
                inputStyle={INPUT_BASE_CLASSNAME}
                maxLength={6}
                inputRegex={amountRegex}
              />
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="mb-4 text-base font-medium text-gray-900">
            Lead Info
          </h3>
          <div className="flex flex-wrap gap-6">
            <div className="w-[48%]">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Deal Value
              </label>
              <Input
                type="number"
                value={formData.deal_value}
                handleInputChange={(value) =>
                  handleInputChange('deal_value', Number(value))
                }
                placeholder="Enter deal value"
                className="w-full"
                error={!!errors.deal_value}
                errorMessage={errors.deal_value}
                inputStyle={INPUT_BASE_CLASSNAME}
              />
            </div>

            <div className="w-[48%]">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Category
              </label>
              <CategoryInput
                value={categoryInput || ''}
                onChange={(val) => setCategoryInput(val)}
                onCategoryChange={handleCategoryChange}
                selectedCategory={
                  formData.category
                    ? apiCategoryOptions.find(
                        (opt) => opt.id === formData.category
                      ) || null
                    : null
                }
                categoryOptions={apiCategoryOptions}
                onUpdateCategory={handleUpdateCategory}
                placeholder="Select/Search Category"
                className="w-full"
                positionFixed
              />
            </div>

            <div className="w-[48%]">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Lead Source
              </label>
              <SourceInput
                value={sourceInput}
                onChange={(val) => setSourceInput(val)}
                onSourceChange={handleSourceChange}
                selectedSource={selectedSource}
                sourceOptions={sourceSelectItems}
                onUpdateSource={handleUpdateSource}
                nonEditableSources={systemSourceNames}
                placeholder="Select or search source"
                className="w-full"
                disabled={isSourceDisabled}
                positionFixed
              />
            </div>

            <div className="w-full">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                onBlur={() => handleBlur('description')}
                placeholder="Enter description"
                className={`h-24 w-full resize-none rounded-md border px-3 py-2 ${INPUT_FOCUS_CLASSNAME} ${
                  errors.description
                    ? 'border-2 border-red-500'
                    : 'border-gray-300'
                }`}
              />
              {errors.description && (
                <p className="text-xs text-red-600">{errors.description}</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3 border-t pt-6">
          <Button
            variant="tertiary"
            label="Cancel"
            handleClick={handleClose}
            className="h-11 px-6"
          />
          <Button
            variant="primary"
            loading={isSaving}
            label="Save"
            handleClick={handleSave}
            className="h-11 px-6"
            disabled={isSaving}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ContactEditModal;
